import { ExportOutlined, ShopOutlined, UserOutlined } from '@ant-design/icons'
import AvailabilityViewer from '@components/availabilityViewer/AvailabilityViewer'
import UserHomeFeed from '@components/availabilityViewer/UserHomeFeed'
import { useHasAccess } from '@components/HasAccess'
import LoyaltyTier from '@components/loyaltyTier/LoyaltyTier'
import Page from '@components/Page/Page'
import { TableFilters } from '@components/TableFilters'
import { TableUrlParams, useTableUrlSync } from '@hooks/useTableUrlSync'
import {
  AvailabilityDiagnosticTableData,
  AvailabilityViewerTableData,
  AVTDTime,
  DiagnosticRawSlotRow,
  UserHomeFeedItem,
} from '@models/Availability'
import { LoyaltyTier as LoyaltyTierModel } from '@models/LoyaltyTier'
import {
  fetchAvailabilityForTier,
  fetchHomeFeedForUser,
} from '@services/AvailabilityViewerService'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { getLoyaltyTiers, selectLoyaltyTiers } from '@store/LoyaltyTierReducer'
import { SBAsyncSelect } from '@supplement-bacon/react-async-select'
import { initialQuery } from '@supplement-bacon/sb-table-filters'
import { Entity } from '@utils/Entities'
import { Permission } from '@utils/permissions'
import {
  Alert,
  App,
  Button,
  Card,
  DatePicker,
  Empty,
  Flex,
  InputNumber,
  Segmented,
  Skeleton,
  Space,
  Switch,
  Table,
  TableColumnsType,
  Tag,
  Typography,
} from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { DEFAULT_ERROR } from '../../../App'

const { Text } = Typography

const AvailabilityViewerPage = () => {
  const { message } = App.useApp()
  const dispatch = useAppDispatch()
  const { hasAccess } = useHasAccess()
  // "By user" mounts a picker over the whole CRM index (GET /users) and then
  // simulates that user's home feed. Roles without `list user` - business and
  // the membership program manager, both of which hold
  // ACCESS_AVAILABILITY_VIEWER - cannot fill the picker, so the mode is not
  // offered to them rather than offered and broken.
  const canSimulateUser = hasAccess(Permission.listUser)
  const [isError, setIsError] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [tableData, setTableData] = useState<AvailabilityViewerTableData>()
  const [diagnostic, setDiagnostic] = useState<boolean>(false)
  const [diagnosticData, setDiagnosticData] =
    useState<AvailabilityDiagnosticTableData>()
  const loyaltyTiers = useAppSelector(selectLoyaltyTiers())
  const [venue, setVenue] = useState<{ name: string; slug: string }>()
  const [homeFeedData, setHomeFeedData] = useState<UserHomeFeedItem[]>()

  // Fonction pour extraire tous les seating areas uniques
  const getAllSeatingAreas = (): string[] => {
    if (!tableData?.times) return []

    const seatingAreasSet = new Set<string>()
    tableData.times.forEach((time) => {
      time.tierTime?.seatingAreas?.forEach((area) => {
        seatingAreasSet.add(area.name)
      })
    })

    return Array.from(seatingAreasSet).sort()
  }

  // Générer les colonnes dynamiquement
  const columns: TableColumnsType<AVTDTime> = [
    {
      title: 'Time',
      key: 'time',
      fixed: 'left',
      width: 100,
      render: (_, record) => <Text>{dayjs(record.from).format('HH:mm')}</Text>,
      onCell: (record, index) => {
        const countWithSameTime = tableData?.times.filter(
          (t) => t.fromIso8601 === record.fromIso8601
        ).length

        if (index === undefined || countWithSameTime === undefined) return {}

        if (index % countWithSameTime === 0) {
          return { rowSpan: countWithSameTime }
        } else {
          return { rowSpan: 0 }
        }
      },
    },
    {
      title: 'Loyalty Tier',
      key: 'tierName',
      fixed: 'left',
      width: 150,
      render: (_, record) => <LoyaltyTier.Tag model={record.tierTime.tier} />,
    },
    // Colonnes dynamiques pour chaque seating area
    ...getAllSeatingAreas().map((areaName) => ({
      title: areaName,
      key: `seatingArea-${areaName}`,
      width: 200,
      render: (_: any, record: AVTDTime) => {
        const area = record.tierTime?.seatingAreas?.find(
          (a) => a.name === areaName
        )

        return area ? (
          <AvailabilityViewer.SeatingArea area={area} />
        ) : (
          <Flex align="center" justify="center">
            <Tag color="red">Unavailable</Tag>
          </Flex>
        )
      },
      onCell: (record: AVTDTime, index?: number) => {
        const area = record.tierTime?.seatingAreas?.find(
          (a) => a.name === areaName
        )

        if (!area || area.slotId == undefined) {
          return {
            style: {
              backgroundColor: '#fff1f0',
            },
          }
        }

        return {
          style: {
            backgroundColor: '#f6ffed',
          },
        }
      },
    })),
  ]

  /**
   * @since 2025-11-11
   * To be noted that isAvailableForBusiness is not implemented as of today as it would require an user with a valid business profile
   */
  const fetchAvailability = async (
    params: TableUrlParams,
    diagnosticEnabled: boolean = diagnostic
  ) => {
    // A stale `f_user` in the URL must not drive the fetch for an admin who may
    // not resolve users: the home-feed endpoint would answer (it only needs
    // ACCESS_AVAILABILITY_VIEWER), but with no picker there is no way to see or
    // change whose feed is on screen.
    const userId =
      canSimulateUser && params.filters.user?.[0]
        ? Number(params.filters.user[0])
        : undefined
    const venueId = params.filters.venue?.[0]
      ? Number(params.filters.venue[0])
      : undefined
    const partySize = params.filters.partySize?.[0]
      ? Number(params.filters.partySize[0])
      : 2
    const formattedDate =
      params.filters.date?.[0] ?? dayjs().format('YYYY-MM-DD')

    // User mode: simulate the app home page for the selected user — every venue they can see,
    // each with availability computed as that user. The venue / tier / diagnostic controls are
    // not used in this mode.
    if (userId) {
      setIsLoading(true)
      setIsError(false)
      try {
        const items = await fetchHomeFeedForUser(
          userId,
          partySize,
          formattedDate
        )
        setHomeFeedData(items)
        setTableData(undefined)
        setDiagnosticData(undefined)
      } catch (e) {
        setIsError(true)
        setHomeFeedData(undefined)
        message.error(DEFAULT_ERROR)
      } finally {
        setIsLoading(false)
      }
      return
    }

    setHomeFeedData(undefined)

    if (!venueId || loyaltyTiers.length === 0) {
      setTableData(undefined)
      setDiagnosticData(undefined)
      return
    }

    setIsLoading(true)
    setIsError(false)

    try {
      const results = await Promise.allSettled([
        fetchAvailabilityForTier(
          venueId,
          partySize,
          formattedDate,
          [],
          diagnosticEnabled
        ),
        ...loyaltyTiers.map((tier) =>
          fetchAvailabilityForTier(
            venueId,
            partySize,
            formattedDate,
            [tier.id],
            diagnosticEnabled
          )
        ),
      ])

      const fulfilled = results.filter((r) => r.status === 'fulfilled')
      if (fulfilled.length === 0) {
        setIsError(true)
        message.error(DEFAULT_ERROR)
        return
      }

      setVenue(fulfilled[0].value.data.venue)

      const tiersWithDefault: LoyaltyTierModel[] = [
        { id: 0, title: 'Default', color: 'default', providers: [] },
        ...loyaltyTiers,
      ]
      const timesMap = new Map<
        string,
        Omit<AVTDTime, 'tierTime'> & {
          tiersTime: AVTDTime['tierTime'][]
        }
      >()
      let isClosedAggregate = true
      let hasAnyAvailability = false

      for (const [index, res] of fulfilled.entries()) {
        const av = res.value.data.availability
        const tier = tiersWithDefault[index]
        // A null availability means Sevenrooms returned nothing / the call failed —
        // NOT that the venue is closed (a closed venue is a non-null availability
        // with isClosed=true). Skip it here and surface it as a fetch error below.
        if (!av) continue
        hasAnyAvailability = true
        isClosedAggregate = isClosedAggregate && !!av.isClosed

        for (const time of av.times ?? []) {
          const key = time.fromIso8601
          const existing = timesMap.get(key)

          if (!existing) {
            timesMap.set(key, {
              from: time.from,
              fromIso8601: time.fromIso8601,
              tiersTime: tiersWithDefault.map((t) => ({
                tier: t,
                seatingAreas: t.id === tier.id ? time.seatingAreas : undefined,
              })),
            })
          } else {
            existing.tiersTime = existing.tiersTime.map((tierTime) =>
              tierTime.tier.id === tier.id
                ? { ...tierTime, seatingAreas: time.seatingAreas }
                : tierTime
            )
          }
        }
      }

      const mergedTimes = Array.from(timesMap.values()).sort(
        (a, b) =>
          dayjs(a.fromIso8601).valueOf() - dayjs(b.fromIso8601).valueOf()
      )

      // No tier returned an availability payload. Outside diagnostic mode this is a
      // genuine fetch/config failure — show the helpful empty state rather than a
      // misleading "closed". In diagnostic mode we keep going so the diagnostic
      // section can surface the actual reason.
      if (!hasAnyAvailability && !diagnosticEnabled) {
        setIsError(true)
        setTableData(undefined)
      } else {
        setTableData({
          isClosed: hasAnyAvailability && isClosedAggregate,
          times: mergedTimes.flatMap((time) =>
            time.tiersTime.map((tierTime) => ({
              ...time,
              tierTime,
            }))
          ),
        })
      }

      if (!diagnosticEnabled) {
        setDiagnosticData(undefined)
      } else {
        const firstDiag = fulfilled
          .map((res) => res.value.data.diagnostic)
          .find((d) => !!d)

        const diagError = fulfilled
          .map((res) => res.value.data.diagnostic?.error)
          .find((err) => !!err)

        if (!firstDiag) {
          setDiagnosticData(undefined)
        } else if (diagError) {
          setDiagnosticData({
            error: diagError,
            displaySlotsNotAttachedToSeatingArea: false,
            seatingAreaMappings: [],
            rows: [],
            tiers: tiersWithDefault,
          })
        } else {
          // Raw SR slots are tier-independent; only their disposition varies per
          // tier. Merge each tier's diagnostic into one row keyed by access rule + time.
          const rowsMap = new Map<string, DiagnosticRawSlotRow>()

          for (const [index, res] of fulfilled.entries()) {
            const diag = res.value.data.diagnostic
            const tier = tiersWithDefault[index]
            if (!diag) continue

            for (const slot of diag.rawSlots ?? []) {
              const key = `${slot.accessRulePersistentId ?? 'none'}__${slot.timeIso}`
              const existing = rowsMap.get(key)

              if (existing) {
                existing.dispositionByTier[tier.id] = {
                  status: slot.status,
                  reason: slot.reason,
                }
              } else {
                rowsMap.set(key, {
                  key,
                  time: slot.time,
                  timeIso: slot.timeIso,
                  type: slot.type,
                  shiftName: slot.shiftName,
                  accessRulePersistentId: slot.accessRulePersistentId,
                  seatingAreaIds: slot.seatingAreaIds,
                  reservationTags: slot.reservationTags,
                  dispositionByTier: {
                    [tier.id]: { status: slot.status, reason: slot.reason },
                  },
                })
              }
            }
          }

          const rows = Array.from(rowsMap.values()).sort(
            (a, b) => dayjs(a.timeIso).valueOf() - dayjs(b.timeIso).valueOf()
          )

          setDiagnosticData({
            displaySlotsNotAttachedToSeatingArea:
              firstDiag.displaySlotsNotAttachedToSeatingArea ?? false,
            seatingAreaMappings: firstDiag.seatingAreaMappings ?? [],
            rows,
            tiers: tiersWithDefault,
            request: firstDiag.request,
            rawResponse: firstDiag.rawResponse,
            httpRequest: firstDiag.httpRequest ?? null,
          })
        }
      }
    } catch (e) {
      setIsError(true)
      message.error(DEFAULT_ERROR)
    } finally {
      setIsLoading(false)
    }
  }

  const { handleFiltersChange, params } = useTableUrlSync({
    onParamsChange: fetchAvailability,
  })

  // Which entity we debug availability for. Seeded from the URL so a deep link with a user
  // filter opens straight in user mode - unless the admin may not resolve users at all, in
  // which case user mode does not exist for them and the seed must not select it.
  const [mode, setMode] = useState<'venue' | 'user'>(() =>
    canSimulateUser && params.filters.user?.[0] ? 'user' : 'venue'
  )

  useEffect(() => {
    dispatch(getLoyaltyTiers(initialQuery))
  }, [])

  useEffect(() => {
    if (loyaltyTiers.length > 0) {
      fetchAvailability(params)
    }
  }, [loyaltyTiers.length])

  const userMode = mode === 'user'
  const hasUser = !!params.filters.user?.[0]
  const hasVenue = !!params.filters.venue?.[0]

  // Switching modes drops the other mode's entity filter so the two stay independent,
  // and clears the now-irrelevant results.
  const handleModeChange = (next: 'venue' | 'user') => {
    if (next === mode) return
    setMode(next)

    const preserved: Record<string, unknown> = {}
    if (params.filters.date?.[0]) preserved.date = params.filters.date[0]
    if (params.filters.partySize?.[0])
      preserved.partySize = Number(params.filters.partySize[0])

    handleFiltersChange(preserved, params.search)

    if (next === 'venue') {
      setHomeFeedData(undefined)
    } else {
      setTableData(undefined)
      setDiagnosticData(undefined)
      setDiagnostic(false)
    }
  }

  return (
    <Page
      permissions={Permission.accessAvailabilityViewer}
      pageTitle={{ title: 'Availability viewer', level: 4 }}
    >
      <Flex align="center" gap="middle" wrap style={{ marginBottom: 12 }}>
        {/* One mode left is not a choice: with `list user` missing the toggle is
            hidden entirely rather than shown as a single dead segment. The mode
            itself is pinned to 'venue' above - antd keeps a controlled value that
            no option matches, so removing the option is not enough. */}
        {canSimulateUser && (
          <Segmented
            value={mode}
            onChange={(val) => handleModeChange(val as 'venue' | 'user')}
            options={[
              { label: 'By venue', value: 'venue', icon: <ShopOutlined /> },
              { label: 'By user', value: 'user', icon: <UserOutlined /> },
            ]}
          />
        )}
        <Text type="secondary">
          {userMode
            ? 'Simulate what a specific user sees on the app home page.'
            : "Inspect a single venue's availability, broken down per loyalty tier."}
        </Text>
      </Flex>

      <TableFilters
        // Re-mount on mode change so the removed entity field is cleared and
        // initialValues are re-applied for the new mode.
        key={mode}
        initialValues={{
          ...(userMode
            ? {
                user: params.filters.user?.[0]
                  ? Number(params.filters.user[0])
                  : undefined,
              }
            : {
                venue: params.filters.venue?.[0]
                  ? Number(params.filters.venue[0])
                  : undefined,
              }),
          partySize: params.filters.partySize?.[0]
            ? Number(params.filters.partySize[0])
            : 2,
          date: params.filters.date?.[0]
            ? dayjs(params.filters.date[0])
            : dayjs(),
        }}
        onValuesChange={(filters: any, search: any) => {
          const { date, ...otherFilters } = filters

          const formattedDate = date
            ? dayjs(date).format('YYYY-MM-DD')
            : undefined

          const newFilters = {
            ...otherFilters,
            ...(formattedDate && { date: formattedDate }),
          }

          handleFiltersChange(newFilters, search)
        }}
        left={[
          userMode
            ? {
                name: 'user',
                type: 'filter',
                label: 'User',
                style: { width: 300 },
                input: (
                  <SBAsyncSelect
                    entity={Entity.user}
                    allowClear
                    placeholder="Type to simulate a user's home feed..."
                    searchFieldName="name"
                  />
                ),
              }
            : {
                name: 'venue',
                type: 'filter',
                label: 'Venue',
                style: { width: 300 },
                input: (
                  <SBAsyncSelect
                    entity={Entity.venue}
                    allowClear
                    placeholder="Type to search venue(s)..."
                    searchFieldName="name"
                  />
                ),
              },
          {
            name: 'date',
            type: 'filter',
            label: 'Date',
            style: { width: 150 },
            input: (
              <DatePicker
                style={{ width: '100%' }}
                format={'DD/MM/YYYY'}
                allowClear={false}
                minDate={dayjs()}
                maxDate={dayjs().add(14, 'day')}
              />
            ),
          },
          {
            name: 'partySize',
            type: 'filter',
            label: 'Party size',
            style: { width: 200 },
            input: (
              <InputNumber
                min={1}
                max={20}
                step={1}
                suffix={
                  <Text type="secondary">
                    <UserOutlined />
                  </Text>
                }
              />
            ),
          },
        ]}
        right={[]}
      />

      {userMode && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 8 }}
          title="Home feed mode"
          description="Showing every venue the selected user can see in the app (membership gating) with availability computed as that user (loyalty-tier slot filtering). The venue and diagnostic controls are ignored in this mode."
        />
      )}

      {!userMode && (
        <Flex
          justify="flex-end"
          align="center"
          gap="small"
          style={{ marginBottom: 8 }}
        >
          <Text type="secondary">Diagnostic mode</Text>
          <Switch
            checked={diagnostic}
            onChange={(checked) => {
              setDiagnostic(checked)
              fetchAvailability(params, checked)
            }}
          />
        </Flex>
      )}

      {userMode ? (
        !hasUser ? (
          <Card>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Select a user to simulate their app home feed."
            />
          </Card>
        ) : isError ? (
          <Card>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Unable to fetch the home feed for this user."
            />
          </Card>
        ) : (
          <UserHomeFeed items={homeFeedData} isLoading={isLoading} />
        )
      ) : !hasVenue ? (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Select a venue to inspect its availability."
          />
        </Card>
      ) : isError ? (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space orientation="vertical" align="center">
                <Text>
                  Unable to fetch availabilities for these values.
                  <br /> Make sure the venue has a valid booking configuration.
                </Text>
                <Link
                  className="btn btn-info"
                  type="button"
                  target="_blank"
                  to={`/venues/${venue?.slug}/booking`}
                >
                  <Button type="primary">
                    Access configuration page for{' '}
                    <Text style={{ color: 'white' }} strong>
                      {venue?.name}
                    </Text>
                    <ExportOutlined />
                  </Button>
                </Link>
              </Space>
            }
          />
        </Card>
      ) : tableData?.isClosed && !diagnosticData?.error ? (
        <Alert
          type="info"
          showIcon
          title="The venue is closed on the selected date."
        />
      ) : isLoading ? (
        <Skeleton active loading={isLoading} />
      ) : (
        <Table
          bordered
          size="small"
          columns={columns}
          pagination={false}
          loading={isLoading}
          dataSource={tableData?.times || []}
          expandable={{ defaultExpandAllRows: true }}
        />
      )}

      {diagnostic && !isLoading && !isError && diagnosticData && (
        <div style={{ marginTop: 16 }}>
          <AvailabilityViewer.Diagnostic data={diagnosticData} />
        </div>
      )}
    </Page>
  )
}

export default AvailabilityViewerPage
